/**
 * "Stufe C" of the Komfort-Login (see keystore.ts): unlocking the saved .p12
 * (Stufe A) via a passkey/biometric instead of typing its passphrase.
 *
 * WebAuthn has no decryption primitive -- an authenticator can only sign
 * challenges, not decrypt arbitrary data. The mechanism that actually makes
 * this possible is the `prf` (pseudo-random function) extension: given a
 * credential and a caller-supplied salt, a `navigator.credentials.get()`
 * call (gated by the platform's biometric prompt) returns a stable,
 * device-bound pseudorandom value. We derive an AES-GCM key from that value
 * via HKDF and use it purely to encrypt/decrypt the .p12 passphrase string
 * that is already sitting in IndexedDB next to the (still passphrase-
 * protected) .p12 bytes.
 *
 * This changes the threat model versus plain Stufe A: the passphrase now
 * persists locally (encrypted), gated by the device's biometric unlock,
 * rather than never touching storage at all -- see README.md.
 *
 * Support for the `prf` extension depends on TWO independent things, and
 * conflating them cost a long debugging session:
 *
 *   1. the browser, which merely relays the extension, and
 *   2. the passkey provider that actually services the prompt, which has to
 *      implement CTAP2 `hmac-secret` to produce the value.
 *
 * `PublicKeyCredential.getClientCapabilities()` reporting `extension:prf:
 * true` only covers (1). Confirmed on a real device (Edge Android 151): with
 * Bitwarden as the provider, both create() and get() return a completely
 * empty `{}` -- no `prf` key at all -- while the identical browser and phone
 * using Google Password Manager returns a proper PRF value. Bitwarden
 * *consumes* PRF (vault unlock, from e.g. a YubiKey) but does not emit it as
 * an Android authenticator. Plain registration/login demos such as
 * webauthn.io never request PRF, so they succeed with Bitwarden and are not
 * evidence that PRF works.
 *
 * Note the failure shape: a provider that understood `prf` but couldn't
 * fulfil it should report `prf: { enabled: false }`. A bare `{}` is the
 * fingerprint of a provider that ignored the extension outright.
 *
 * Hence `hints: ["client-device"]` below, steering users to a capable
 * authenticator. `setupPasskeyProtection` still throws a descriptive error
 * if PRF is unavailable, and callers must offer the plain-passphrase path
 * regardless.
 *
 * IMPORTANT, found the hard way: `navigator.credentials.create()` reporting
 * `prf.enabled: true` does NOT guarantee `prf.results` is populated at
 * registration time -- the spec treats evaluating `eval` during create() as
 * optional even for browsers that fully support the extension, and several
 * real implementations only ever evaluate it during get(). Relying on
 * create()'s results (as an earlier version of this file did) meant setup
 * could "succeed" (no error thrown, `enabled` was true) while the passphrase
 * never actually got encrypted with a reproducible key -- unlock would then
 * have no way to work later. We now always follow create() with an
 * immediate get() to obtain the real PRF output, which is the pattern the
 * spec authors and every working PRF demo actually use.
 */

const HKDF_INFO = new TextEncoder().encode("signit-p12-passphrase-v1");

export interface PasskeyProtection {
  credentialId: ArrayBuffer;
  salt: ArrayBuffer;
  iv: ArrayBuffer;
  ciphertext: ArrayBuffer;
}

export function isWebAuthnAvailable(): boolean {
  return typeof window !== "undefined" && !!window.PublicKeyCredential && !!navigator.credentials;
}

function randomBytes(length: number): Uint8Array<ArrayBuffer> {
  return crypto.getRandomValues(new Uint8Array(new ArrayBuffer(length)));
}

async function aesKeyFromPrfOutput(prfOutput: ArrayBuffer): Promise<CryptoKey> {
  const hkdfKey = await crypto.subtle.importKey("raw", prfOutput, "HKDF", false, ["deriveKey"]);
  return crypto.subtle.deriveKey(
    { name: "HKDF", hash: "SHA-256", salt: new Uint8Array(0), info: HKDF_INFO },
    hkdfKey,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

/** Extension results typing lags the spec in lib.dom.d.ts; the shape here
 * matches the WebAuthn Level 3 `prf` extension. */
interface PrfExtensionResults {
  prf?: { enabled?: boolean; results?: { first?: ArrayBuffer } };
}

/** Prompts for the passkey (biometric) and evaluates the PRF extension for
 * `salt`, returning the raw pseudorandom output. This is the only place
 * that actually needs a successful `prf.results.first` -- both setup
 * (called right after create()) and later unlock go through here. */
async function evalPrf(credentialId: ArrayBuffer, salt: ArrayBuffer): Promise<ArrayBuffer> {
  const assertion = (await navigator.credentials.get({
    publicKey: {
      challenge: randomBytes(32),
      allowCredentials: [{ id: credentialId, type: "public-key" }],
      userVerification: "required",
      extensions: { prf: { eval: { first: salt } } } as AuthenticationExtensionsClientInputs,
    },
  })) as PublicKeyCredential;

  const ext = assertion.getClientExtensionResults() as PrfExtensionResults;
  console.debug("[signit passkey] get() extension results", ext);
  const prfOutput = ext.prf?.results?.first;
  if (!prfOutput) {
    // Kept short and actionable on purpose -- the protocol detail behind this
    // (provider must implement CTAP2 hmac-secret) is in the module comment and
    // the README, not in the user's face.
    throw new Error(
      "Dieser Passkey funktioniert hier nicht. Bitte beim Einrichten die " +
        "Bildschirmsperre des Geräts wählen, nicht einen Passwortmanager.",
    );
  }
  return prfOutput;
}

/**
 * Registers a new passkey, bound to this device's platform authenticator,
 * then immediately performs a get() to obtain and use the actual PRF value
 * to encrypt `passphrase` (see the module comment for why create()'s own
 * results can't be trusted). This means two biometric prompts in a row for
 * setup -- worse UX than one, but the two-step approach is what actually
 * works reliably across browsers.
 */
export async function setupPasskeyProtection(
  rpName: string,
  accountLabel: string,
  passphrase: string,
): Promise<PasskeyProtection> {
  const salt = randomBytes(32);
  const credential = (await navigator.credentials.create({
    publicKey: {
      challenge: randomBytes(32),
      rp: { name: rpName, id: location.hostname },
      user: { id: randomBytes(16), name: accountLabel, displayName: accountLabel },
      pubKeyCredParams: [
        { type: "public-key", alg: -7 }, // ES256
        { type: "public-key", alg: -257 }, // RS256
      ],
      // Pinned to a platform (device-bound) authenticator on purpose: a
      // synced/roaming passkey (e.g. saved to a password manager account)
      // may not support the prf extension even when the device's own
      // biometric sensor does -- see the module comment for the Bitwarden
      // case that proved this.
      authenticatorSelection: {
        authenticatorAttachment: "platform",
        userVerification: "required",
        residentKey: "required",
      },
      // WebAuthn L3 `hints` steers the OS credential sheet towards the
      // device's own authenticator (screen lock / Google Password Manager)
      // instead of a third-party password-manager provider. On Android
      // `authenticatorAttachment: "platform"` alone is not enough, since
      // third-party providers also register themselves as platform
      // authenticators. Ignored by browsers that predate `hints`.
      hints: ["client-device"],
      extensions: { prf: {} } as AuthenticationExtensionsClientInputs,
    } as PublicKeyCredentialCreationOptions & { hints: string[] },
  })) as PublicKeyCredential;

  // Deliberately NOT gating on this: `enabled` being false/absent here does
  // not reliably mean the extension is unsupported -- some implementations
  // only ever populate results via a subsequent get(). evalPrf() below is
  // the actual, authoritative check (it throws its own descriptive error).
  console.debug(
    "[signit passkey] create() extension results",
    credential.getClientExtensionResults(),
  );

  const prfOutput = await evalPrf(credential.rawId, salt.buffer);
  const key = await aesKeyFromPrfOutput(prfOutput);
  const iv = randomBytes(12);
  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    new TextEncoder().encode(passphrase),
  );

  return { credentialId: credential.rawId, salt: salt.buffer, iv: iv.buffer, ciphertext };
}

/**
 * Prompts for the passkey (biometric) and decrypts the passphrase that was
 * encrypted in `setupPasskeyProtection`.
 */
export async function unlockPassphraseWithPasskey(protection: PasskeyProtection): Promise<string> {
  const prfOutput = await evalPrf(protection.credentialId, protection.salt);
  const key = await aesKeyFromPrfOutput(prfOutput);
  const plaintext = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: protection.iv },
    key,
    protection.ciphertext,
  );
  return new TextDecoder().decode(plaintext);
}
